import React from 'react';
import { getContentControl, translateToProps } from './ContentControls';
import ContentControlHOC from './ContentControlHOC';

/* Custom element factories used from elements/ */
import AccordionFactory from './elements/AccordionFactory';
import TableFactory from './elements/TableFactory';

/*
 * Some elements require a factory to handle unique parsing situations.
 * Their data is not structured like other controls and may/may not contain other nested controls
 * inside of the data structure.
 *
 * If `recurse` is true, the parser will look to use a provided factory
 */

const ContentElementTypes = {
  DataRequest: {
    recurse: false,
  },
  Table: {
    recurse: true,
    factory: TableFactory,
  },
  Accordion: {
    recurse: true,
    factory: AccordionFactory,
  },
};

function isComplexElement(name) {
  const el = ContentElementTypes[name];
  return el && el.recurse === true && el.factory !== undefined ? true : false;
}
/**
 * @description
 *
 * Please note that in the absence of @delegateOnly or @delegateOnly="false",
 * the datarequest should be visible to an applicant as well as to delegate both but only when the @delegateOnly="true"
 * then that datarequest should be visible to delegate only and not to an applicant.
 *
 * rules to display
 * visble = false => false
 * delegateOnly = false =>  isDelegate = false => true
 * delegateOnly = false =>  isDelegate = true  => true
 * delegateOnly = true  =>  isDelegate = true  => true
 * delegateOnly = true  =>  isDelegate = false => false
 *
 * @param {boolean} visible
 * @param {boolean} delegateOnly
 * @param {boolean} isDelegate This attribute come from session storage
 * @returns {boolean}
 */

function validateIsVisible(visible, delegateOnly, isDelegate) {
  if (delegateOnly === true && !isDelegate) {
    return false;
  }
  return visible;
}

function isTableElement(name) {
  return name === 'Table';
}

function isReviewerOnly(el) {
  // Applicant View return el && el.props.reviewerOnly === true ? true : false;
  return false;
}

function isElementExcluded(excludedList, node) {
  return excludedList !== undefined && (excludedList.indexOf(node.elementType) !== -1 || excludedList.indexOf(node.controlTypeName) !== -1)
    ? true
    : false;
}

function getContentElement(name) {
  return ContentElementTypes[name] || null;
}

function isValidFactory(obj) {
  if (obj.component === undefined || obj.elements === undefined || !Array.isArray(obj.elements) || typeof obj.component !== 'function') {
    return false;
  } else {
    return true;
  }
}

function isRootElement(el) {
  return el.isRootLevel ? true : false;
}

function createOnChangeWrapper(onChangeFn, setConditionalFn, elementId) {
  return event => {
    setConditionalFn(elementId, event);
    onChangeFn(event.target.value); // might be a problem what if a plain value is provided instead of event.target?
  };
}

function getParentElement(node, elements, elementsIndex) {
  const { parentId } = node;

  if (!parentId) return null;

  const parentPosition = elementsIndex[parentId];
  const parentNode = elements[parentPosition];

  return parentNode;
}

/*
 * Some element types (factories) require the formValue to be translated so that the backend
 * service can update appropriately
 */
function transformElementValue(node, value, { elements, elementsIndex }, isUpdated = true) {
  const { parentId, logicId } = node;
  const parentPosition = parentId && elementsIndex[parentId];

  if (parentPosition !== undefined) {
    const parentNode = elements[parentPosition];
    const { controlTypeName: parentControlType, isTable } = parentNode;
    const baseOptions = {
      isDeleted: false,
      isRowLocked: false,
      isUpdated,
      ...(node.isNewRow && { isNewRow: node.isNewRow }),
      elementName: logicId,
      value,
    };

    if (!isTable) return value;

    switch (parentControlType) {
      case 'Horziontal':
        return {
          ...baseOptions,
        };
      case 'Entity':
        return {
          ...baseOptions,
          ...(node.answerPageId && { answerPageId: node.answerPageId }),
          ...(node.sourceTableId && { sourceTableId: node.sourceTableId }),
        };
      case 'HTCombo':
        return {
          ...baseOptions,
          docTypeCode: node.docTypeCode,
        };
      default:
        return {
          ...baseOptions,
        };
    }
  }

  return value;
}

function createElementFromCopy(sourceElement, newValue, actions, index, idSuffix = '', otherProps = {}, otherComponentProps = {}) {
  const { setConditionalFn, setUpdateComponentFn, setComponentRef, onChangeFn, onUpdateState } = actions;

  const newElementId = sourceElement.id + index + idSuffix;
  const controlResult = getContentControl(sourceElement.controlTypeName);

  const sourceValue = sourceElement.props.value ? sourceElement.props.value : '';
  const elValue = newValue || sourceValue;
  const newElement = {
    ...sourceElement,
    logicId: sourceElement.id,
    id: newElementId,
    onChange: onChangeFn ? createOnChangeWrapper(onChangeFn, setConditionalFn, newElementId) : setConditionalFn.bind(null, newElementId),
    component: ContentControlHOC(controlResult.component, setUpdateComponentFn(newElementId), setComponentRef(newElementId), onUpdateState),
    props: {
      ...sourceElement.props,
      id: newElementId,
      name: newElementId,
      value: elValue,
      ...otherComponentProps,
    },
    ...otherProps,
  };

  return newElement;
}

/**
 *
 * @param {*} node
 * @param {*} props
 * @param {*} isRootLevel
 * @param {*} actions
 * @param {*} otherProps
 * @returns
 */

function createElementEntry(node, props, isRootLevel, actions, otherProps = {}) {
  const { conditionalWatch, getLookupValue, getOverrides, setUpdateComponent, setComponentRef, elementActions } = actions;
  const { rootNumberOfColumns, readOnlyControl, profileCountryCode } = otherProps;
  const controlResult = getContentControl(node.controlTypeName, readOnlyControl);
  if (!controlResult && !isComplexElement(node.elementType)) return null;

  const {
    elementName: id,
    displayText: label,
    showLogic,
    complexShowLogic,
    numberOfColumns,
    controlTypeName,
    showMe: visible,
    callPersist,
  } = node;

  const controlOverrides = getOverrides();
  const displayUI = node.displayUI === 'Y';
  const docTypeCode = node.docTypeCode;
  const delegateOnly = node.delegateOnly;
  let sourceComponent = controlOverrides[controlTypeName] || (controlResult && controlResult.component);
  let finalComponent = null;

  const controlProps = translateToProps(node);
  const customControlProps = controlResult && controlResult.translateProps ? controlResult.translateProps(node, props) : {};

  // Depedency inject setUpdateComponent() from ContentParser. This will result in a reference callback
  // from the ContentControlHOC which will allow the ContentParser to setState() on ContentControlHOC's
  if (!isComplexElement(node.elementType)) {
    finalComponent = ContentControlHOC(sourceComponent, setUpdateComponent(id), setComponentRef(id));
  }

  let dropDownOptions = {};
  let showLogicLookUp = null;

  if (node.lookUpName && node.lookUpName !== '') {
    const lookUpValue = getLookupValue(node.lookUpName);
    dropDownOptions = { listOptions: lookUpValue };

    /*
     * The BE team has stated that for lookUp values it can NOT use the `lookUpItemValue` when evaluating showLogic.
     * Instead, we must work around this issue and use the `displayText` as the evaluating value.
     * A little confusing, but apparently this can not be changed.
     *
     * 12/18/2018 - An edge case was added on CountryList lookups. The property `lookupCode` needs to be used
     *              instead of displayText
     *
     */
    if (lookUpValue) {
      showLogicLookUp = lookUpValue.reduce((acc, next) => {
        //let lookupIndex = next.lookupCode ? next.lookupCode : next.displayText;
        let lookupIndex = next.lookupCode ? next.lookupCode : next.lookUpBindValue.split('|')[1];
        acc[lookupIndex] = next.lookUpBindValue;
        return acc;
      }, {});
    }
  }

  // these are needed for the entity table
  const { pageId, sourceTableId } = props.data;

  const quoteControlProps =
    controlTypeName === 'Quote'
      ? {
          quoteStatus: node.quoteStatus,
          currCode: node.currCode,
          quote: node.quote,
        }
      : {};
  
  const questionnarieTextboxCharRestrict =
    controlTypeName === "Textbox"
      ? {
          additionalCharRestict: props.additionalCharRestict,
          allowedSpecialChars: props.allowedSpecialChars,
          allowedValidationMessageSpecialChars: props.allowedValidationMessageSpecialChars,
          isSplCharRestriction: true
        }
      : {};

  return {
    id,
    label,
    controlTypeName,
    isRootLevel,
    isTable: isTableElement(node.elementType),
    isCallPersist: callPersist,
    component: finalComponent,
    displayUI: displayUI,
    logicId: id,
    onChange: conditionalWatch.bind(null, id),
    showLogic: showLogic || complexShowLogic,
    showLogicLookUp,
    complexShowLogic,
    updateComponent: () => {},
    pageId: pageId || null,
    sourceTableId: sourceTableId || null,
    delegateOnly,
    docTypeCode,
    ignoreFormValue: controlResult && controlResult.ignoreFormValue ? controlResult.ignoreFormValue : false,
    props: {
      ...controlProps,
      ...customControlProps,
      ...dropDownOptions,
      ...(controlResult && controlResult.includePageId && { pageId: props.pageId }),
      controlTypeName,
      isComponentVisible: visible !== undefined ? visible : true,
      fullWidth: isComplexElement(node.elementType) || controlResult.fullWidth ? true : false,
      complexShowLogic,
      numberOfColumns,
      rootNumberOfColumns,
      profileCountryCode,
      origControlTypeName: node.controlTypeName,
      increaseFontSize: node.increaseFontSize,
      helpText: node.helpText,
      icon: node.icon,
      align: node.align ? node.align : 'L',
      showText: node.showText ? node.showText : false,
      smartAddLookupName: node.smartAddLookupName ? node.smartAddLookupName : false,
      smartLookUpSearchable: node.smartLookUpSearchable ? node.smartLookUpSearchable : false,
      addressLookUpCountry: node.addressLookUpCountry ? node.addressLookUpCountry : false,
      biggerIcon: node.biggerIcon === true && (node.align === 'C' || node.align === 'L') ? true : false,
      elementActions: elementActions,
      isCallPersist: callPersist,
      reloadPage: node.reloadPage,
      ...quoteControlProps,
      ...questionnarieTextboxCharRestrict
    },
  };
}

function getElementFromStateById(id, index, elementActions, showLabels = false) {
  const { getParserState } = elementActions();
  const { elements, logicIndex } = getParserState();

  if (!logicIndex[id]) {
    return null;
  }

  const positions = logicIndex[id];
  const elId = id + index;
  let elementMatch = false;

  for (let cIndex = 0; cIndex < positions[cIndex]; cIndex++) {
    const curPosition = positions[cIndex];
    if (elements[curPosition].id === elId) {
      elementMatch = elements[curPosition];
      break;
    }
  }

  if (!elementMatch) return false;

  const Component = elementMatch.component;
  return <Component key={elementMatch.id} onChange={elementMatch.onChange} showLabels={showLabels} {...elementMatch.props} />;
}

function getElementComponent(el, index, showLabels = false, pageId, additionalProps = {}) {
  if (!el.component) return null;

  const Component = el.component;
  return <Component key={index} onChange={el.onChange} showLabels={showLabels} {...el.props} {...additionalProps} />;
}

export {
  getContentElement,
  getElementComponent,
  getElementFromStateById,
  getParentElement,
  createElementEntry,
  createElementFromCopy,
  isRootElement,
  isReviewerOnly,
  isElementExcluded,
  isComplexElement,
  isValidFactory,
  transformElementValue,
  ContentElementTypes,
};
